import type { AlarmGroup, AlertGroup, LaneInterval, LaneRowData, LaneSectionData } from '../types'

export function normalizeSeverity(severity: string): string {
  const s = severity?.toLowerCase() ?? 'informational'
  if (s.includes('critical') || s.includes('alarm')) return 'critical'
  if (s.includes('warn')) return 'warning'
  return 'informational'
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatTooltip(raisedMs: number, clearedTime: string | undefined, severity: string): string {
  const sevLabel = capitalize(severity)
  const started = `Started ${new Date(raisedMs).toLocaleString()}`
  if (clearedTime) {
    return `${sevLabel} \u2014 ${started}, Cleared ${new Date(clearedTime).toLocaleString()}`
  }
  return `${sevLabel} \u2014 ${started}, Active`
}

/** Build the Alarms lane section — one row per alarm description, multiple intervals per row */
export function buildAlarmsSection(alarms: AlarmGroup[]): LaneSectionData {
  const rows: LaneRowData[] = []

  for (let i = 0; i < (alarms ?? []).length; i++) {
    const alarm = alarms[i]
    const sev = normalizeSeverity(alarm.severity)
    const label = alarm.description || `Alarm ${i + 1}`
    const intervals: LaneInterval[] = alarm.times.map((t) => {
      const startMs = new Date(t.raisedTime).getTime()
      const endMs = t.clearedTime ? new Date(t.clearedTime).getTime() : Date.now()
      return {
        start: startMs,
        end: endMs,
        value: sev,
        tooltip: formatTooltip(startMs, t.clearedTime, sev),
      }
    })
    rows.push({
      key: `alarm_${i}`,
      label,
      colorMapKey: 'alarm',
      intervals,
    })
  }

  return { title: `Alarms (${(alarms ?? []).length})`, rows }
}

/** Build the Alerts lane section — one row per alert name, multiple intervals per row */
export function buildAlertsSection(alerts: AlertGroup[]): LaneSectionData {
  const rows: LaneRowData[] = []

  for (let i = 0; i < (alerts ?? []).length; i++) {
    const alert = alerts[i]
    const sev = normalizeSeverity(alert.severity)
    const label = alert.name || `Alert ${i + 1}`
    const intervals: LaneInterval[] = alert.times.map((t) => {
      const startMs = new Date(t.raisedTime).getTime()
      const endMs = t.clearedTime ? new Date(t.clearedTime).getTime() : Date.now()
      return {
        start: startMs,
        end: endMs,
        value: sev,
        tooltip: formatTooltip(startMs, t.clearedTime, sev),
      }
    })
    rows.push({
      key: `alert_${i}`,
      label,
      colorMapKey: 'alert',
      intervals,
    })
  }

  return { title: `Alerts (${(alerts ?? []).length})`, rows }
}
